import React, { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiArrowRight } from 'react-icons/fi';
import { publicService } from '../services';
import { Card } from './ui';

interface AdItem {
  id: string;
  titulo: string;
  subtitulo?: string;
  linkUrl?: string;
}

// ── Pastel palette ─────────────────────────────────────────────────────────
const PASTELS = [
  {
    bg: 'linear-gradient(135deg, #EDE0FF 0%, #D8C4FF 100%)',
    dot: 'rgba(155,109,255,0.18)',
    accent: '#7C3AED',
    tag: '#F3EBFF',
    tagText: '#6D28D9',
    emoji: '⚡',
  },
  {
    bg: 'linear-gradient(135deg, #FFE8F3 0%, #FFC8E4 100%)',
    dot: 'rgba(236,72,153,0.18)',
    accent: '#DB2777',
    tag: '#FFF0F8',
    tagText: '#9D174D',
    emoji: '🎯',
  },
  {
    bg: 'linear-gradient(135deg, #E0F7FF 0%, #BAE8FF 100%)',
    dot: 'rgba(14,165,233,0.18)',
    accent: '#0284C7',
    tag: '#F0FAFF',
    tagText: '#075985',
    emoji: '💡',
  },
  {
    bg: 'linear-gradient(135deg, #E6FFF4 0%, #BBF7D0 100%)',
    dot: 'rgba(16,185,129,0.18)',
    accent: '#059669',
    tag: '#F0FFF8',
    tagText: '#065F46',
    emoji: '🚀',
  },
  {
    bg: 'linear-gradient(135deg, #FFF9E0 0%, #FEF08A 100%)',
    dot: 'rgba(234,179,8,0.18)',
    accent: '#D97706',
    tag: '#FFFBE8',
    tagText: '#92400E',
    emoji: '🏅',
  },
  {
    bg: 'linear-gradient(135deg, #FFF0E8 0%, #FDCBA8 100%)',
    dot: 'rgba(249,115,22,0.18)',
    accent: '#EA580C',
    tag: '#FFF5EF',
    tagText: '#9A3412',
    emoji: '🎁',
  },
];

// ── SVG graphic per slide index ─────────────────────────────────────────────
const Graphic: React.FC<{ index: number; accent: string }> = ({ index, accent }) => {
  const variants = [
    // 0 — dots grid
    <svg key="dots" className="absolute inset-0 w-full h-full" aria-hidden>
      <defs>
        <pattern id={`dots-${index}`} x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.5" fill={accent} opacity="0.18" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#dots-${index})`} />
      <circle cx="88%" cy="18%" r="52" fill={accent} opacity="0.07" />
      <circle cx="92%" cy="75%" r="34" fill={accent} opacity="0.09" />
    </svg>,
    // 1 — diagonal lines
    <svg key="lines" className="absolute inset-0 w-full h-full" aria-hidden>
      <defs>
        <pattern id={`lines-${index}`} x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="18" stroke={accent} strokeWidth="1.2" opacity="0.14" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#lines-${index})`} />
      <circle cx="85%" cy="50%" r="60" fill={accent} opacity="0.08" />
    </svg>,
    // 2 — circles
    <svg key="circles" className="absolute inset-0 w-full h-full" aria-hidden>
      <circle cx="90%" cy="10%" r="70" fill={accent} opacity="0.07" />
      <circle cx="80%" cy="80%" r="40" fill={accent} opacity="0.06" />
      <circle cx="95%" cy="55%" r="24" stroke={accent} strokeWidth="2" fill="none" opacity="0.14" />
      <circle cx="70%" cy="15%" r="10" stroke={accent} strokeWidth="1.5" fill="none" opacity="0.16" />
    </svg>,
    // 3 — wave
    <svg key="wave" className="absolute inset-0 w-full h-full" aria-hidden viewBox="0 0 400 160" preserveAspectRatio="none">
      <path d="M0,80 Q50,40 100,80 T200,80 T300,80 T400,80 L400,160 L0,160Z" fill={accent} opacity="0.07" />
      <path d="M0,100 Q80,60 160,100 T320,100 T400,100 L400,160 L0,160Z" fill={accent} opacity="0.05" />
      <circle cx="370" cy="30" r="28" fill={accent} opacity="0.08" />
    </svg>,
    // 4 — hex grid
    <svg key="hex" className="absolute inset-0 w-full h-full" aria-hidden>
      <defs>
        <pattern id={`hex-${index}`} x="0" y="0" width="28" height="24" patternUnits="userSpaceOnUse">
          <polygon points="14,2 26,9 26,21 14,28 2,21 2,9" fill="none" stroke={accent} strokeWidth="0.8" opacity="0.18" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#hex-${index})`} />
      <circle cx="88%" cy="50%" r="45" fill={accent} opacity="0.08" />
    </svg>,
    // 5 — sparkles
    <svg key="sparkles" className="absolute inset-0 w-full h-full" aria-hidden>
      {[
        [320, 20, 8], [350, 70, 5], [290, 100, 6], [370, 120, 4], [310, 145, 7],
      ].map(([cx, cy, r], i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill={accent} opacity="0.15" />
      ))}
      <circle cx="88%" cy="18%" r="48" fill={accent} opacity="0.07" />
    </svg>,
  ];
  return <>{variants[index % variants.length]}</>;
};

// ── Component ───────────────────────────────────────────────────────────────
const PublicidadeCard: React.FC = () => {
  const [ads, setAds] = useState<AdItem[]>([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [animating, setAnimating] = useState(false);

  useEffect(() => { loadAds(); }, []);

  useEffect(() => {
    if (ads.length <= 1) return;
    const t = setInterval(() => goTo((c) => (c + 1) % ads.length), 10000);
    return () => clearInterval(t);
  }, [ads.length]);

  const loadAds = async () => {
    try {
      const data = await publicService.getAdvertisingSpots();
      if (data.length > 0) {
        setAds((data as any[]).map((ad: any) => ({
          id: String(ad.ID || ad.id || Math.random()),
          titulo: ad.TITULO || ad.titulo || '',
          subtitulo: ad.SUBTITULO || ad.subtitulo || '',
          linkUrl: ad.URL || ad.LINK_URL || ad.linkUrl || undefined,
        })));
      } else {
        setAds(defaultAds());
      }
    } catch {
      setAds(defaultAds());
    } finally {
      setLoading(false);
    }
  };

  const defaultAds = (): AdItem[] => [
    { id: '1', titulo: 'Pague contas sem sair de casa', subtitulo: 'Água, luz, telefone e mais — tudo num clique', linkUrl: '/payments' },
    { id: '2', titulo: 'O seu dinheiro, a trabalhar por si', subtitulo: 'Cashback em cada pagamento', linkUrl: '/cashback' },
    { id: '3', titulo: 'Os melhores eventos de Cabo Verde', subtitulo: 'Bilhetes online, entrega instantânea', linkUrl: '/eventos' },
  ];

  const goTo = (updater: number | ((c: number) => number)) => {
    setAnimating(true);
    setTimeout(() => {
      setCurrent(typeof updater === 'function' ? updater : () => updater);
      setAnimating(false);
    }, 180);
  };

  const handleClick = () => {
    const url = ads[current]?.linkUrl;
    if (!url) return;
    if (url.startsWith('http')) window.open(url, '_blank');
    else window.location.href = url;
  };

  if (loading) {
    return (
      <Card className="overflow-hidden" padding="none">
        <div className="h-[170px] animate-pulse" style={{ background: PASTELS[0].bg }} />
      </Card>
    );
  }

  const ad = ads[current];
  const p = PASTELS[current % PASTELS.length];

  return (
    <Card className="overflow-hidden" padding="none">
      <div className="relative h-[170px] select-none">
        {/* Slide */}
        <button
          onClick={handleClick}
          className={`w-full h-full text-left group transition-opacity duration-200 ${animating ? 'opacity-0' : 'opacity-100'}`}
          style={{ background: p.bg }}
        >
          {/* Background graphic */}
          <Graphic index={current} accent={p.accent} />

          {/* Content */}
          <div className="relative h-full flex flex-col justify-between pl-12 pr-10 py-4">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold self-start"
              style={{ background: p.tag, color: p.tagText }}
            >
              <span>{p.emoji}</span>
              <span>Pagali</span>
            </div>

            {/* Headline */}
            <div>
              <h3
                className="font-extrabold text-[17px] leading-tight mb-1"
                style={{ color: p.accent }}
              >
                {ad?.titulo}
              </h3>
              {ad?.subtitulo && (
                <p className="text-sm leading-snug opacity-75" style={{ color: p.accent }}>
                  {ad.subtitulo}
                </p>
              )}
            </div>

            {/* CTA row */}
            {ad?.linkUrl && (
              <div
                className="inline-flex items-center gap-1 text-xs font-semibold"
                style={{ color: p.accent }}
              >
                <span>Saber mais</span>
                <FiArrowRight className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            )}
          </div>
        </button>

        {/* Prev / Next */}
        {ads.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goTo((c) => (c - 1 + ads.length) % ads.length); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-white transition-colors"
              style={{ color: p.accent }}
            >
              <FiChevronLeft size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goTo((c) => (c + 1) % ads.length); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-white transition-colors"
              style={{ color: p.accent }}
            >
              <FiChevronRight size={14} />
            </button>
          </>
        )}

        {/* Dots */}
        {ads.length > 1 && (
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5">
            {ads.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); goTo(i); }}
                className="rounded-full transition-all"
                style={{
                  width: i === current ? 16 : 6,
                  height: 6,
                  background: i === current ? p.accent : `${p.accent}44`,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default PublicidadeCard;
